import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import type { CanvasSuggestion } from "@ando/core";
import { cn } from "@ando/ui";
import type { I18n } from "@lingui/core";
import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Check, Loader2, Sparkles, X } from "lucide-react";
import * as React from "react";

/**
 * SuggestedCanvasCard — dense pill row in the "Consigliati" section.
 *
 * May 30, 2026 rewrite — was a 90px-tall gradient card; reshaped into a
 * ~36px single-line pill so the section scales to 100+ suggestions without
 * dominating the hub. Visual weight reduced (white card, thin orange-soft
 * border, sparkle icon as accent — no full gradient) to keep the list
 * scannable; per-row inline signals collapse to a single meta line.
 *
 * Idempotency: parent owns the mutation hooks; this card just reports
 * intent. ``isApproving`` / ``isRejecting`` flags drive the inline
 * spinners — both buttons disable while either action is in flight so
 * the user can't double-fire.
 */
export interface SuggestedCanvasCardProps {
  suggestion: CanvasSuggestion;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  isApproving?: boolean;
  isRejecting?: boolean;
}

/**
 * Render a compact, human-friendly summary of the score breakdown.
 *
 * The BE emits a dict like ``{sources:3, days_active:7, participants:2}``;
 * we project it into 2-3 chips that read as Italian phrases. Skips zero
 * entries so the chip strip never shows "0 fonti" which would undermine
 * the recommendation.
 */
function describeSignals(
  i18n: I18n,
  breakdown: Record<string, number | boolean>,
): readonly string[] {
  const out: string[] = [];

  const sources = breakdown.sources;
  if (typeof sources === "number" && sources > 0) {
    out.push(
      i18n._(
        plural(sources, {
          one: "# fonte",
          other: "# fonti",
        }),
      ),
    );
  }

  const days = breakdown.days_active ?? breakdown.time_spread_days;
  if (typeof days === "number" && days > 0) {
    out.push(`${days}g`);
  }

  const participants = breakdown.participants ?? breakdown.distinct_users;
  if (typeof participants === "number" && participants > 0) {
    out.push(
      i18n._(
        plural(participants, {
          one: "# persona",
          other: "# persone",
        }),
      ),
    );
  }

  return out;
}

export const SuggestedCanvasCard: React.FC<SuggestedCanvasCardProps> = ({
  suggestion,
  onApprove,
  onReject,
  isApproving = false,
  isRejecting = false,
}) => {
  const { i18n, t } = useLingui();
  const busy = isApproving || isRejecting;
  const signals = describeSignals(i18n, suggestion.score_breakdown);
  const seen = formatRelativeTime(suggestion.created_at);
  const metaParts = [...signals, ...(seen ? [t`vista ${seen}`] : [])];
  const metaTitle = metaParts.join(" · "); // full text on hover via title

  return (
    <li
      role="article"
      aria-labelledby={`suggested-canvas-${suggestion.id}-title`}
      className={cn(
        "group flex items-center gap-2.5 rounded-md",
        "border border-hairline/60 bg-transparent px-2 py-1.5",
        "transition-colors hover:border-ando-orange/40",
      )}
    >
      <span aria-hidden className="flex shrink-0 items-center justify-center text-ando-orange">
        <Sparkles size={13} strokeWidth={2} />
      </span>

      <div className="flex min-w-0 flex-1 items-baseline gap-2">
        <span
          id={`suggested-canvas-${suggestion.id}-title`}
          className="truncate font-medium text-[12.5px] text-ink-1"
        >
          {suggestion.entity_name}
        </span>
        <span className="shrink-0 font-semibold text-[9.5px] text-ink-3 uppercase tracking-[0.06em]">
          {suggestion.entity_type}
        </span>
        {metaParts.length > 0 ? (
          // Two-tier meta visibility (May 31, 2026 UX review): on narrow
          // widths surface AT LEAST the first signal (almost always
          // "N fonti" — the strongest justification for the suggestion),
          // and reveal the full strip from ``md`` up where horizontal
          // room makes the noise affordable. Full string is on the
          // ``title`` attr regardless.
          <span title={metaTitle} className="truncate text-[11px] text-ink-3 tabular-nums">
            <span>{metaParts[0]}</span>
            {metaParts.length > 1 ? (
              <span className="hidden md:inline">
                {metaParts.slice(1).map((p) => (
                  <React.Fragment key={p}>
                    <span aria-hidden> · </span>
                    {p}
                  </React.Fragment>
                ))}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => onApprove(suggestion.id)}
          disabled={busy}
          aria-label={t`Crea canvas per ${suggestion.entity_name}`}
          className={cn(
            "inline-flex h-7 items-center gap-1 rounded px-2.5",
            "bg-ando-orange font-medium text-[11.5px] text-white",
            "transition-colors hover:bg-ando-orange-light",
            "disabled:cursor-not-allowed disabled:opacity-60",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-ando-orange focus-visible:ring-offset-1",
            "focus-visible:ring-offset-cream",
          )}
        >
          {isApproving ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Check size={12} strokeWidth={2.5} />
          )}
          <Trans>Crea</Trans>
        </button>

        <button
          type="button"
          onClick={() => onReject(suggestion.id)}
          disabled={busy}
          aria-label={t`Ignora suggerimento per ${suggestion.entity_name}`}
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded text-ink-3",
            "transition-colors hover:bg-black/[0.04] hover:text-ink-1",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-ando-orange focus-visible:ring-offset-1",
            "focus-visible:ring-offset-cream",
          )}
        >
          {isRejecting ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <X size={12} strokeWidth={2.25} />
          )}
        </button>
      </div>
    </li>
  );
};
SuggestedCanvasCard.displayName = "SuggestedCanvasCard";
